import * as Interface_DTO_Draw from 'app/ts/Interface_DTO_Draw';
import * as Enums from 'app/ts/clientDto/Enums';
import * as Client from 'app/ts/clientDto/index';
import { InteriorLogic } from 'app/ts/services/ConfigurationLogic/InteriorLogic';
import { ISnapInfoService } from 'app/ts/services/snap/ISnapInfoService';
import { VectorHelper } from 'app/ts/util/VectorHelper';
import Enumerable from 'linq';
import { BaseSnapService } from './BaseSnapService';

/**
 * Helper to provide info about snap positions. Only expected to last from dragStart to dragEnd
 */
export class InteriorAdaptionPanelSnapService extends BaseSnapService<Client.SnapInfo> {
  static supportsItems(items: Client.ConfigurationItem[]): boolean {
    return (
      items.length === 1 &&
      items[0].snapLeftAndRight &&
      items[0].snapOnFloorOrBottomCorpusPanel
    );
  }

  private readonly possiblePositions: InteriorAdaptionPanelSnapService.PositionInfo[];
  private readonly suggestedOffsets: Interface_DTO_Draw.Vec2d[];
  private readonly mouseOffset: Interface_DTO_Draw.Vec2d;
  private otherItemCubes: Client.ItemCube[];

  constructor(
    cabinetSection: Client.CabinetSection,
    item: Client.ConfigurationItem,
    dragStartPoint: Interface_DTO_Draw.Vec2d,
    fallbackSnapService: ISnapInfoService,
  ) {
    super(cabinetSection, item, dragStartPoint, fallbackSnapService);

    this.otherItemCubes = cabinetSection.interior.itemCubes
      .filter((cube) => cube.item !== this.item)
      .filter(
        (cube) =>
          !cube.item.isDummy &&
          !cube.item.isTemplate &&
          !cube.item.isFittingPanel &&
          !cube.item.isVerticalDivider &&
          !cube.item.isGrip,
      )
      .filter((cube) => cube.Z < item.frontZ);

    this.mouseOffset = VectorHelper.subtract(
      dragStartPoint,
      item as Interface_DTO_Draw.Vec2d,
    );

    this.possiblePositions = this.getPossiblePositions(item);
    this.suggestedOffsets = this.possiblePositions.map((p) =>
      this.toOffset(p, this.item),
    );
  }

  private getPossiblePositions(
    item: Client.ConfigurationItem,
  ): InteriorAdaptionPanelSnapService.PositionInfo[] {
    const result: InteriorAdaptionPanelSnapService.PositionInfo[] = [];

    let gableGaps = InteriorLogic.getGableGaps(this.cabinetSection, {
      excludeItems: [this.item],
      includeFittingPanels: false,
      ignorePulloutSeverity: true,
      ignoreDrillings: true,
    });

    let allowAllProducts =
      this.cabinetSection.cabinet.floorPlan.FullCatalogAllowOtherProducts;
    let allowAllMaterials =
      this.cabinetSection.cabinet.floorPlan.FullCatalogAllowOtherMaterials;
    let suitableGaps = gableGaps.filter((gap) =>
      this.item.Product!.isAvailableInWidth(
        gap.Width,
        allowAllProducts,
        this.cabinetSection.cabinet.ProductLineId,
        allowAllMaterials,
        this.item.MaterialId,
      ),
    );

    for (let gap of suitableGaps) {
      // Dimensions
      let maxHeight = this.cabinetSection.interior.cube.Height;
      if (!!gap.leftGable) {
        maxHeight = Math.min(maxHeight, gap.leftGable.Height);
      }
      if (!!gap.rightGable) {
        maxHeight = Math.min(maxHeight, gap.rightGable.Height);
      }

      let lowestOtherItem = Enumerable.from(this.otherItemCubes)
        .where((otherItemCube) => VectorHelper.overlapsX(otherItemCube, gap))
        .orderBy((itemCube) => itemCube.Y)
        .firstOrDefault();

      if (!!lowestOtherItem)
        maxHeight = Math.min(
          maxHeight,
          lowestOtherItem.Y - this.cabinetSection.interior.cube.Y,
        );

      if (maxHeight < item.minHeight) continue;

      // Position
      let frontZ =
        this.cabinetSection.InteriorDepth + this.cabinetSection.backing.frontZ;
      if (!!gap.leftGable) {
        frontZ = Math.min(frontZ, gap.leftGable.frontZ);
      }
      if (!!gap.rightGable) {
        frontZ = Math.min(frontZ, gap.rightGable.frontZ);
      }

      let position = {
        X: gap.X,
        Y: this.cabinetSection.interior.cube.Y,
        Z: frontZ - item.depthReduction - item.Depth,
      };

      result.push({
        ...position,
        Height: maxHeight,
        Width: gap.Width,
        Depth: item.Depth,
      });
    }

    return result;
  }

  protected overriddenGetSnapInfo(
    position: Interface_DTO_Draw.Vec2d,
  ): Client.SnapInfo | undefined {
    let rawItemPos = VectorHelper.subtract(position, this.mouseOffset);

    if (!this.possiblePositions || this.possiblePositions.length === 0) {
      return;
    }

    let sortedSuggestions = Enumerable.from(this.possiblePositions)
      .orderBy((possiblePos) =>
        VectorHelper.contains(position, possiblePos) ? 0 : 1,
      )
      .thenBy((p) => Math.abs(p.X - rawItemPos.X));

    let bestSuggestion = sortedSuggestions.first();

    let _dropOffset = {
      ...VectorHelper.subtract(bestSuggestion, this.item),
      Z: 0,
    };

    let _result: Client.SnapInfo = {
      dropOffset: _dropOffset,
      pulloutRestriction: Enums.PulloutWarningSeverity.None,
      rulers: [],
      suggestedItemOffsets: this.suggestedOffsets,
      newSize: {
        X: bestSuggestion.Width,
        Y: bestSuggestion.Height,
        Z: bestSuggestion.Depth,
      },
      mirror: false,
      horizontalGuidelineHeights: [],
      snapsTo: [],
      snappingItems: [this.item],
    };

    return _result;
  }

  protected overriddenPlace(
    lastSnapInfo: Client.SnapInfo,
  ): [Client.ConfigurationItem] {
    if (!!lastSnapInfo) {
      this.item.X = Math.round(this.item.X);
      this.item.Y = Math.round(this.item.Y);
      this.item.Z = Math.round(this.item.Z);

      if (!!lastSnapInfo.newSize) {
        if (!!lastSnapInfo.newSize.X)
          this.item.trySetWidth(lastSnapInfo.newSize.X);
        if (!!lastSnapInfo.newSize.Y)
          this.item.trySetHeight(lastSnapInfo.newSize.Y);
        if (!!lastSnapInfo.newSize.Z)
          this.item.trySetDepth(lastSnapInfo.newSize.Z);
      }
    }

    return [this.item];
  }

  toOffset(
    position: Interface_DTO_Draw.Rectangle,
    item: Client.ConfigurationItem,
  ): any {
    let _result = VectorHelper.subtract(position, item);
    _result.X = Math.round(_result.X);
    _result.Y = Math.round(_result.Y);
    return _result;
  }
}

export module InteriorAdaptionPanelSnapService {
  export interface PositionInfo extends Interface_DTO_Draw.Cube {}
}
